import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Cpu, Delete, LogIn, Store, Warehouse, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, setBranch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [step, setStep] = useState('pin'); // 'pin' | 'branch'
  const [branches, setBranches] = useState([]);

  const MAX_PIN = 4;

  const handleDigit = (digit) => {
    if (pin.length < MAX_PIN) {
      setPin(prev => prev + digit);
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleSubmit = async () => {
    if (pin.length !== MAX_PIN) {
      toast.error('Ingresa tu PIN de 4 digitos');
      return;
    }
    setLoading(true);
    try {
      await login(pin);
      toast.success('Bienvenido!');
      // Fetch branches and decide next step
      const res = await axios.get(`${API}/branches/public`).catch(() => ({ data: [] }));
      const list = res.data || [];
      if (list.length === 0) {
        // Should not happen because backend seeds Bodega Central, but fall back gracefully
        setBranch(null);
        navigate('/admin');
        return;
      }
      if (list.length === 1) {
        setBranch(list[0]);
        navigate('/admin');
        return;
      }
      setBranches(list);
      setStep('branch');
    } catch (error) {
      const message = error.response?.data?.detail || 'PIN invalido';
      const detail = typeof message === 'string' ? message : Array.isArray(message) ? message.map(e => e.msg || JSON.stringify(e)).join(' ') : String(message);
      toast.error(detail);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handlePickBranch = (b) => {
    setBranch(b);
    toast.success(`Sucursal: ${b.name}`);
    navigate('/admin');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && pin.length === MAX_PIN) {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen login-bg" onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="min-h-screen login-overlay flex items-center justify-center p-4">
        <div className="w-full max-w-sm animate-fade-in">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/20 mb-3">
              <Cpu className="w-7 h-7 text-primary" />
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_d0ebb01a-3628-4f72-9656-9b72303a57b5/artifacts/fdp6yzo5_logo.png" 
              alt="Supertech" 
              className="h-10 mx-auto mb-1"
            />
            <p className="text-slate-400 text-sm">Sistema de Inventario</p>
          </div>

          {/* PIN Card */}
          {step === 'pin' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-5 text-center font-['Plus_Jakarta_Sans']" data-testid="login-title">
              Ingresa tu PIN
            </h2>

            {/* PIN Dots */}
            <div className="flex justify-center gap-4 mb-6" data-testid="pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all duration-200 ${
                    i < pin.length 
                      ? 'bg-primary scale-110 shadow-lg shadow-primary/30' 
                      : 'bg-slate-600/50 border border-slate-500'
                  }`}
                />
              ))}
            </div>

            {/* Hidden input for mobile numeric keyboard fallback */}
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, MAX_PIN);
                setPin(val);
              }}
              className="sr-only"
              autoFocus
              data-testid="pin-hidden-input"
            />

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-5" data-testid="pin-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleDigit(String(num))}
                  className="h-14 rounded-xl bg-slate-700/50 hover:bg-slate-600/70 active:bg-primary/30 text-white text-xl font-semibold transition-all duration-150 active:scale-95 border border-slate-600/30"
                  data-testid={`pin-key-${num}`}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={handleDelete}
                className="h-14 rounded-xl bg-slate-700/30 hover:bg-red-500/20 active:bg-red-500/30 text-slate-400 hover:text-red-400 transition-all duration-150 active:scale-95 flex items-center justify-center border border-slate-600/30"
                data-testid="pin-key-delete"
              >
                <Delete className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => handleDigit('0')}
                className="h-14 rounded-xl bg-slate-700/50 hover:bg-slate-600/70 active:bg-primary/30 text-white text-xl font-semibold transition-all duration-150 active:scale-95 border border-slate-600/30"
                data-testid="pin-key-0"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={pin.length !== MAX_PIN || loading}
                className={`h-14 rounded-xl transition-all duration-150 active:scale-95 flex items-center justify-center font-semibold border ${
                  pin.length === MAX_PIN 
                    ? 'bg-primary hover:bg-primary/90 text-white border-primary/50' 
                    : 'bg-slate-700/30 text-slate-500 border-slate-600/30 cursor-not-allowed'
                }`}
                data-testid="pin-key-enter"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <LogIn className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="p-3 bg-slate-900/30 rounded-lg">
              <p className="text-xs text-slate-400 text-center">
                Ingresa tu PIN de 4 digitos para acceder al sistema
              </p>
            </div>
          </div>
          )}

          {/* Branch selector */}
          {step === 'branch' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-2 text-center font-['Plus_Jakarta_Sans']">
              Selecciona la sucursal
            </h2>
            <p className="text-xs text-slate-400 text-center mb-5">
              Vas a trabajar en esta sucursal durante esta sesión.
            </p>
            <div className="space-y-2.5" data-testid="branch-selector">
              {branches.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => handlePickBranch(b)}
                  className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-700/40 hover:bg-primary/20 active:bg-primary/30 ring-1 ring-slate-600/40 hover:ring-primary/50 text-left transition-all"
                  data-testid={`branch-pick-${b.id}`}
                >
                  <span className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${b.is_warehouse ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}`}>
                    {b.is_warehouse ? <Warehouse className="w-5 h-5" /> : <Store className="w-5 h-5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{b.name}</p>
                    <p className="text-slate-400 text-xs truncate">
                      {b.is_warehouse ? 'Bodega central' : (b.address || 'Sucursal')}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Back to shop */}
          <div className="mt-5 text-center">
            <a href="/" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Volver a la tienda
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
